import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import get from "lodash/get";
import { Button, SvgIconHandler } from "../../atoms";
import { STATIC_KEYWORDS } from "../../../constants";
import { SocialShareTemplate } from "../../../arrow";
import { SocialShare } from "@quintype/components";
import { useDeviceType } from "../../customHooks/useDeviceType";
import { GridView } from "./grid-view";
import { ListView } from "./list-view";
import SearchIcon from "../rankings-table/search";
import { YearDropdown } from "./year-drop-down";
import ListingAdSlot from "../../molecules/ListingAdSlot";
import { trackLoadMoreClick } from "../../utils";

import "./all-rankings.m.css";

export const MostPowerfulWomen = ({ data, config }) => {
  const cdnImage = `https://${config?.["cdn-image"]}`;
  const {
    sortedEntitiesByYearJson,
    snapshotByRank,
    rankingEntitySlug,
    entitiesByYearJson,
    entitiesByYear,
    sortedYears,
    year,
    staticData,
  } = data;
  const { detailPageTitle, rankingDescription } = sortedYears.find((obj) => obj.yearObj.slug === year);
  const { label, top15Label } = staticData;
  const { LOAD_MORE, READ_MORE, NO_RESULT_FOUND, TRY_AGAIN } = STATIC_KEYWORDS;
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";

  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const isAdfreePage = get(qtState, ["config", "subscriber"]) === "true";

  const [listView, toggleListView] = useState(false);
  const [enableLoadMore, setEnableLoadMore] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [readMoreClicked, setReadMoreClicked] = useState(false);
  const [yearDropdownActive, setYearDropdownActive] = useState(false);
  const [activeYear, setActiveYear] = useState(year);
  const [visibleItemsForGrid, setVisibleItemsForGrid] = useState(snapshotByRank.length);
  const [visibleItemsForList, setVisibleItemsForList] = useState(snapshotByRank.length);
  const [displayedPeople, setDisplayedPeople] = useState(snapshotByRank);
  const [searchCount, setSearchCount] = useState(snapshotByRank.length);

  const subheadlineRef = useRef(null);
  const searchInputRef = useRef(null);
  const [shareURL, setShareURL] = useState("");
  const expandBio = () => setReadMoreClicked(true);

  useEffect(() => {
    !isDesktop ? setVisibleItemsForGrid(8) : setVisibleItemsForGrid(16);
    setVisibleItemsForList(10);
  }, [isDesktop]);

  const handleSearch = (e) => {
    e.preventDefault();
    const searchTerm = searchInputRef.current.value;
    setSearchText(searchTerm);
    if (searchTerm) {
      trackRankingInteraction("ranking_search", searchTerm.toLowerCase());
    }
  };

  const getMoreStories = () => {
    trackLoadMoreClick({
      linkCategory: `${rankingEntitySlug}-${year}`,
      category: "ranking",
      subCategory: rankingEntitySlug,
      templateName: "rankdetail",
    });
    listView
      ? setVisibleItemsForList((prev) => prev + visibleItemsForList)
      : setVisibleItemsForGrid((prev) => prev + visibleItemsForGrid);
  };

  const startsWithCaseInsensitive = (str, prefix) => str.toLowerCase().startsWith(prefix.toLowerCase());

  useEffect(() => {
    setEnableLoadMore((listView ? visibleItemsForList : visibleItemsForGrid) < snapshotByRank.length);
  }, [visibleItemsForGrid, visibleItemsForList]);

  useEffect(() => {
    if (!yearDropdownActive || isDesktop) {
      document.body.classList.remove("no-scroll");
    } else if (yearDropdownActive && !isDesktop) {
      setTimeout(() => {
        document.body.classList.add("no-scroll");
      }, 100);
    }
    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [yearDropdownActive]);

  useEffect(() => {
    setShareURL(window.location.href);
    setTimeout(() => {
      const urlPath = new URL(window.location.href).pathname.split("/");
      const activeYear = urlPath[urlPath.length - 1];
      setActiveYear(activeYear);
    }, 100);
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "dl_page_info",
        cms_author_name: "NA",
        byline: "NA",
        category: "rankings",
        sub_category:
          data?.rankingEntitySlug?.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()) || "NA",
        content_id: "NA",
        template_name: "rankdetail",
        publish_date: "NA",
        publish_time: "NA",
        login_status: "not-logged-in", // will be changed
        subscription_status: "not-subscribed", // will be changed
        ad_free: false, // will be changed
        login_wall_enabled: false,
        platform: window.innerWidth >= 768 ? "Desktop" : "Mobile",
      });
    }
  }, []);

  useEffect(() => {
    setEnableLoadMore(snapshotByRank.length > (listView ? visibleItemsForList : visibleItemsForGrid));
  }, [listView, visibleItemsForList, visibleItemsForGrid, snapshotByRank.length]);

  useEffect(() => {
    document.body.classList.remove("no-scroll");
  }, [yearDropdownActive]);

  useEffect(() => {
    if (!isDesktop && subheadlineRef.current) {
      const bioHeight = window?.getComputedStyle(subheadlineRef.current);
      const getLineHeight = parseFloat(bioHeight.lineHeight);
      const lineHeightForThree = getLineHeight * 3;
      const contentHeight = subheadlineRef.current.scrollHeight;
      subheadlineRef.current.style.height = `${lineHeightForThree}px`;
      subheadlineRef.current.style.overflow = "hidden";

      if (readMoreClicked) {
        subheadlineRef.current.style.height = `${contentHeight}px`;
      }
    }
  }, [readMoreClicked, isDesktop]);

  useEffect(() => {
    let entities = snapshotByRank || [];
    if (searchText) {
      entities = entities.filter((entity) => startsWithCaseInsensitive(entity.name, searchText));
    }
    if (!searchText) setDisplayedPeople(entities);
    setSearchCount(entities.length);
    setDisplayedPeople(entities);
  }, [snapshotByRank, searchText]);

  // Track all ranking detail interactions
  let isTracking = false;
  const trackRankingInteraction = (action, value, position = null) => {
    if (isTracking) return;
    isTracking = true;
    setTimeout(() => {
      isTracking = false;
    }, 300);
    if (typeof window !== "undefined" && window.dataLayer) {
      window.dataLayer.push({
        event: "dl_ranking_detail_interactions",
        event_action: action,
        link_text: value,
        ...(position && { link_position: position }),
        category: "rankings",
        sub_category: data?.rankingEntitySlug || "NA",
        template_name: "rankdetail",
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };

  // Use generic tracking function for view change
  const trackViewChange = (viewType) => {
    trackRankingInteraction("grid_option", viewType.toLowerCase());
  };

  const buttons = [
    {
      type: "grid",
      isActive: !listView,
      toggle: () => {
        toggleListView(false);
        trackViewChange("grid");
      },
    },
    {
      type: "list",
      isActive: listView,
      toggle: () => {
        toggleListView(true);
        trackViewChange("list");
      },
    },
  ];

  const logoName = {
    "40under40": !isDesktop ? "/images/banners/40under40Mobile.png" : "/images/banners/40under40Desktop.png",
    "most-powerful-women": !isDesktop ? "/images/banners/MpwMobile.png" : "/images/banners/MpwDesktop.png",
  };

  return (
    <div styleName="entity-detail-page">
      <div
        styleName="banner"
        // style={{
        //   backgroundImage: `url("${logoName[data.rankingEntitySlug]}")`,
        // }}
      ><img src={logoName[data.rankingEntitySlug]} width={1470} height={417} alt={detailPageTitle} title={detailPageTitle} loading="lazy" /></div>
      <div className="container" styleName="content-wrapper">
        <div styleName="header">
          <div styleName="header-content">
            <div styleName="headline">
              <span styleName="yeardrop">
                <YearDropdown
                  isDesktop={isDesktop}
                  yearDropdownActive={yearDropdownActive}
                  setYearDropdownActive={setYearDropdownActive}
                  yearData={sortedEntitiesByYearJson}
                  activeYear={activeYear}
                  setActiveYear={setActiveYear}
                  rankingEntitySlug={rankingEntitySlug}
                  entitiesByYearJson={entitiesByYearJson}
                  entitiesByYear={entitiesByYear}
                  onYearChange={(year) => trackRankingInteraction("year_filter_applied", year)}
                />
              </span>
              <h1 styleName="text">{detailPageTitle}</h1>
            </div>
            <div styleName="sub-headline clamped-text" ref={subheadlineRef}>
              {rankingDescription}
            </div>
            {!readMoreClicked && !isDesktop && (
              <button onClick={expandBio} styleName="read-more">
                {READ_MORE}
              </button>
            )}
          </div>
        </div>
        <div styleName="search-row">
          <form onSubmit={handleSearch}>
            <div styleName="search">
              <div styleName="input">
                {searchText && searchInputRef?.current?.value !== "" && (
                  <span
                    onClick={() => {
                      setSearchText("");
                      searchInputRef.current.value = "";
                    }}
                  >
                    <SvgIconHandler type="close" height="12px" width="12px" viewBox="0 0 12 12" />
                  </span>
                )}
                <input type="text" name="search" placeholder="Search" ref={searchInputRef} />
              </div>
              <Button customStyleName={"bg-brand-primary"}>{isDesktop ? "Search" : <SearchIcon />}</Button>
            </div>
          </form>
          <div styleName="list-grid-toggle">
            {buttons.map(({ type, isActive, toggle }, index) => (
              <span styleName={`${index === 1 ? "border-right" : "border-left"} ${isActive ? "active" : ""}`}>
                <Button
                  key={type}
                  backgroundColor={isActive ? "rgb(var(--black))" : undefined}
                  svgColor={isActive ? { color: "rgb(var(--white))" } : undefined}
                  iconType={type}
                  iconWidth={18}
                  iconHeight={18}
                  onClickFunction={toggle}
                />
              </span>
            ))}
            <span styleName="mpw-share">
              <SocialShare
                popupText="Share this URL"
                template={(props) =>
                  SocialShareTemplate({
                    ...props,
                    onShareClick: (platform) =>
                      trackRankingInteraction("share_click", platform?.toLowerCase() || "share"),
                  })
                }
                fullUrl={encodeURI(shareURL)}
                title={`${detailPageTitle}`}
                shareIconId={`share-icon-company-`}
              />
            </span>
          </div>
        </div>
        {displayedPeople.length < 1 ? (
          searchText === "" ? (
            <div style={{ minHeight: "300px" }}></div>
          ) : (
            <div styleName="oops-text">
              <span>
                {NO_RESULT_FOUND}'{searchText}' <br /> {TRY_AGAIN}
              </span>
            </div>
          )
        ) : (
          <div styleName={`page-body ${!listView ? "grid-view" : ""}`}>
            <div style={{ display: listView ? "none" : "contents" }}>
              <GridView
                snapshotByRank={displayedPeople}
                visibleItemsForGrid={visibleItemsForGrid}
                rankingEntitySlug={rankingEntitySlug}
                cdnImage={cdnImage}
                isSsr={true}
                isRankingDetail={true}
                eventAction="profile_select"
              />
            </div>
            <div style={{ display: listView ? "contents" : "none" }}>
              <ListView
                snapshotByRank={displayedPeople}
                visibleItemsForList={visibleItemsForList}
                rankingEntitySlug={rankingEntitySlug}
              />
            </div>
          </div>
        )}
        {enableLoadMore && searchCount > 16 && (
          <div styleName="load-more-button">
            <Button onClickFunction={() => getMoreStories()}>{LOAD_MORE}</Button>
          </div>
        )}
        {!isAdfreePage && (
          <ListingAdSlot
            adType="above_footer"
            pageType={label === "Most Powerful Women" ? "most-powerful-women" : "40-under-40"}
            adStyleName="ad-slot-size-728x90-300x250"
            index={3}
          />
        )}
      </div>
    </div>
  );
};
